'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import { API_ENDPOINTS } from '@/config/api';
import Icon from '@/components/ui/AppIcon';
import { setAuthCookie } from '@/utils/auth';
import OrganizationDropdown from '@/components/common/OrganizationDropdown';

interface LoginFormProps {
  onSuccess?: () => void;
}

interface FormData {
  organization: string;
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  organization?: string;
  email?: string;
  password?: string;
  general?: string;
}

const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    organization: '',
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);


  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    if (field === 'organization' && typeof value === 'string') {
      if (!value) {
        setErrors((prev) => ({ ...prev, organization: 'Organization is required' }));
      } else {
        setErrors((prev) => ({ ...prev, organization: undefined }));
      }
    }

    if (field === 'email' && typeof value === 'string') {
      if (value && !validateEmail(value)) {
        setErrors((prev) => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
    }
    
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: FormErrors = {};
    
    if (!formData.organization) {
      newErrors.organization = 'Organization is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(API_ENDPOINTS.auth.signin(), {
        username: formData.email,
        password: formData.password,
        organizationName: formData.organization,
      });

      const { data, success, message } = response.data;

      if (success && data) {
        if (isHydrated) {
          // Always clear existing tokens before storing new ones to ensure freshness
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userName');
          
          // Store fresh token and refresh token from API response
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('refreshToken', data.refreshToken);
          
          // NEW: Store in cookie for middleware
          setAuthCookie(data.token);
          
          // Map API role to UI role
          let displayRole = 'Associate';
          const apiRole = data.roles && data.roles.length > 0 ? data.roles[0] : '';
          
          if (apiRole === 'ROLE_ADMIN') displayRole = 'Admin';
          else if (apiRole === 'ROLE_MANAGER') displayRole = 'Manager';
          else displayRole = 'Associate';

          localStorage.setItem('userRole', displayRole);
          
          // Store user name
          // Store user name
          const fullName = data.firstName && data.lastName 
            ? `${data.firstName} ${data.lastName}`
            : data.firstName || data.username.split('@')[0];
          localStorage.setItem('userName', fullName);
          localStorage.setItem('userId', data.id.toString());

          // Only store email if remember me is checked
          if (formData.rememberMe) {
            localStorage.setItem('userEmail', formData.email);
          } else {
            localStorage.removeItem('userEmail');
          }
          
          // Force UserContext to update everywhere
          window.dispatchEvent(new Event('storage'));
        }
        
        onSuccess?.();
        router.push('/dashboard');
      } else {
        setErrors({
          general: message || 'Authentication failed. Please check your credentials.',
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Invalid credentials or server error. Please try again.';
      setErrors({
        general: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.organization) {
      setErrors({ organization: 'Please select your organization first' });
      return;
    }
    if (!formData.email) {
      setErrors({ email: 'Please enter your email address first' });
      return;
    }
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await axios.post(API_ENDPOINTS.auth.forgotPassword(), {
        email: formData.email,
        organizationName: formData.organization,
      });
      
      setErrors({ general: 'A password reset link has been sent to your email.' });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send reset link. Please try again.';
      setErrors({ general: message });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isHydrated) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-card rounded-lg shadow-elevation-3 p-8 border border-border">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="space-y-4">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-5">
          {errors.general && (
            <div className={`${errors.general.includes('sent') ? 'bg-success/10 border-success text-success' : 'bg-error/10 border-error text-error'} border rounded-md p-4 flex items-start gap-3`}>
              <Icon 
                name={errors.general.includes('sent') ? "CheckCircleIcon" : "ExclamationTriangleIcon"} 
                size={20} 
                variant="solid" 
                className="flex-shrink-0 mt-0.5" 
              />
              <p className="text-sm font-caption">{errors.general}</p>
            </div>
          )}

          <OrganizationDropdown
            value={formData.organization}
            onChange={(val) => handleInputChange('organization', val)}
            error={errors.organization}
            disabled={isLoading}
          />

          <div>
            <label htmlFor="email" className="block text-sm font-caption font-medium text-foreground mb-2">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-muted-foreground" />
              </div>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 bg-background border rounded-md font-caption text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${
                  errors.email ? 'border-error' : 'border-border'
                }`}
                placeholder="you@example.com"
                disabled={isLoading}
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-error font-caption">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-caption font-medium text-foreground mb-2">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="KeyIcon" size={20} variant="outline" className="text-muted-foreground" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`w-full pl-10 pr-12 py-3 bg-background border rounded-md font-caption text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${
                  errors.password ? 'border-error' : 'border-border'
                }`}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={isLoading}
              >
                <Icon
                  name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                  size={20}
                  variant="outline"
                  className="text-muted-foreground hover:text-foreground transition-smooth"
                />
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-error font-caption">{errors.password}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-ring transition-smooth"
                disabled={isLoading}
              />
              <span className="text-sm font-caption text-foreground">Remember me</span>
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm font-caption text-primary hover:text-primary/80 transition-smooth"
              disabled={isLoading}
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground py-3 rounded-xl font-caption font-semibold text-base hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform hover:scale-[1.02]"
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                <span>Signing In...</span>
              </>
            ) : (
              <>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        {/* SSO Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-3 text-muted-foreground font-caption tracking-wider">or continue with</span>
          </div>
        </div>

        {/* SSO Buttons */}
        <div className="grid grid-cols-3 gap-3">
          {/* Google */}
          <button
            type="button"
            onClick={() => {
              setErrors({ general: 'Google SSO is coming soon. Please use email login for now.' });
            }}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-background border border-border rounded-xl text-sm font-caption font-medium text-foreground hover:bg-muted/50 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="hidden sm:inline">Google</span>
          </button>

          {/* Microsoft */}
          <button
            type="button"
            onClick={() => {
              setErrors({ general: 'Microsoft SSO is coming soon. Please use email login for now.' });
            }}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-background border border-border rounded-xl text-sm font-caption font-medium text-foreground hover:bg-muted/50 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
              <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
              <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
              <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
              <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
            </svg>
            <span className="hidden sm:inline">Microsoft</span>
          </button>

          {/* GitHub */}
          <button
            type="button"
            onClick={() => {
              setErrors({ general: 'GitHub SSO is coming soon. Please use email login for now.' });
            }}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-background border border-border rounded-xl text-sm font-caption font-medium text-foreground hover:bg-muted/50 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            <span className="hidden sm:inline">GitHub</span>
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm font-caption text-muted-foreground">
            Don&apos;t have an account?{' '}
            <button
              type="button"
              className="text-primary hover:text-accent font-semibold transition-smooth underline underline-offset-2 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-primary/30 shadow-sm hover:scale-105"
              onClick={() => router.push('/signup')}
            >
              Sign Up
            </button>
          </p>
        </div>
    </div>
  );
};

export default LoginForm;